import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import i18n from '../lib/i18n';
import { reportClientError } from '../lib/errorReporting';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// After a new deploy, a browser tab left open on the old index.html
// still tries to lazy-load a JS chunk by its old (now-deleted) hashed
// filename, which 404s as "Failed to fetch dynamically imported module".
// A plain reload fixes it (fresh index.html -> current chunk hashes), so we
// do that once automatically instead of showing "something went wrong" for
// what is really just a stale-tab problem. The sessionStorage flag stops a
// reload loop if the reload doesn't actually help (e.g. a real network
// outage) - CampIdag.tsx clears it again after a clean mount so a *future*
// deploy can still trigger one auto-reload in a long-lived tab.
const CHUNK_LOAD_ERROR_PATTERN = /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed/i;
export const CHUNK_RELOAD_FLAG_KEY = 'campidag_chunk_reload_attempted';

/**
 * Catches render-time errors anywhere below it in the tree, reports them to
 * the audit log (source: 'error-boundary'), and shows a fallback instead of
 * an unmounted white screen. Uncaught exceptions/rejections outside of React
 * rendering are handled separately by ErrorReporter.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isChunkLoadError = CHUNK_LOAD_ERROR_PATTERN.test(error.message);

    // Benign stale-tab-after-deploy: an old tab tried to load a since-deleted
    // chunk hash. Reload ONCE to pick up the fresh index.html + current hashes.
    // Don't report it - it self-heals, and reporting would flood the audit log
    // with a bogus client_error on every deploy. Only if the reload already ran
    // and it's STILL failing (flag set) do we treat it as a real problem below.
    if (isChunkLoadError && !sessionStorage.getItem(CHUNK_RELOAD_FLAG_KEY)) {
      sessionStorage.setItem(CHUNK_RELOAD_FLAG_KEY, '1');
      window.location.reload();
      return;
    }

    // A genuine render error, or a chunk error that survived the auto-reload
    // (stale CDN/index.html, offline, adblocker, etc.) - worth logging.
    reportClientError(error.message, {
      stack: error.stack,
      source: 'error-boundary',
      context: {
        componentStack: errorInfo.componentStack ?? undefined,
        chunkLoadError: isChunkLoadError || undefined,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col justify-center items-center h-screen bg-surface px-6 text-center gap-4">
          <h1 className="text-xl font-bold font-serif text-on-surface">
            {i18n.t('errorBoundary.title')}
          </h1>
          <p className="text-sm text-on-surface-variant max-w-md">
            {i18n.t('errorBoundary.message')}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 bg-primary text-on-primary font-bold rounded-xl px-4 py-2 text-sm hover:opacity-90 active:scale-95 transition-all cursor-pointer"
          >
            <RefreshCw className="w-4 h-4" />
            {i18n.t('errorBoundary.reload')}
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
